// Copyright (c) 2025 Cozy Intellect Inc.
// File Path: packages/sharedlib/src/stores/draftFlow.store.ts

import { writable, derived } from 'svelte/store';
import type { Readable } from 'svelte/store';
import type {
  ActionObj,
  ActionTile,
  WorkflowField,
  WorkflowSelectedData
} from '@repo/common/types/ActionWorkflow';
import type { ActionType } from '@repo/common/types/TaskSynthesis';
import type { DraftInputContent } from '@repo/common/types/Editor';
import type { ActionObjBase, ActionObjType } from '@repo/common/types/ActionWorkflow';
import type { Writable } from 'svelte/store';

export interface StepDataBase<T extends ActionObjBase | ActionObjType> {
  mode: 'single' | 'multi';
  stepNumber: number;
  fieldName: WorkflowField;
  tiles: ActionTile[];
  items: ActionObj<T>[];
  originalPositions?: { x: number; y: number; width: number; height: number }[];
}

export interface StepDataSingle<
  T extends ActionObjBase | ActionObjType = ActionObjBase | ActionObjType
> extends StepDataBase<T> {
  selectedLabel: string | null;
  selectedIndex: number | null;
}

export interface StepDataMulti<
  T extends ActionObjBase | ActionObjType = ActionObjBase | ActionObjType
> extends StepDataBase<T> {
  selectedLabels: string[];
  selectedIndices: number[];
}

export type StepData<T extends ActionObjBase | ActionObjType = ActionObjBase | ActionObjType> =
  | StepDataSingle<T>
  | StepDataMulti<T>;

// Individual stores for each piece of state
export const selectedActionType: Writable<ActionType | null> = writable<ActionType | null>(null);
// Complete set of all steps in the flow (never modified after creation)
export const completeDraftFlowSteps = writable<StepData[]>([]);
// Support both single and multi-select values
export const draftFlowSelectedData = writable<WorkflowSelectedData>({} as WorkflowSelectedData);
export const draftFlowInputContent = writable<DraftInputContent | null>(null);
export const draftFlowIsComplete = writable<boolean>(false);
export const draftFlowAutoAdvance = writable<boolean>(true);
// Flag to indicate if the flow was started from chat input
export const draftFlowStartedFromChat = writable<boolean>(false);

export const draftFlowCurrentStepIndex = writable<number>(0);
export const draftFlowCurrentStep: Readable<StepData | null> = derived(
  [completeDraftFlowSteps, draftFlowCurrentStepIndex],
  ([$completeDraftFlowSteps, $draftFlowCurrentStepIndex]) => {
    return $completeDraftFlowSteps?.[$draftFlowCurrentStepIndex] || null;
  }
);
export const currentStepData = derived(
  [draftFlowSelectedData, draftFlowCurrentStep],
  ([$draftFlowSelectedData, $draftFlowCurrentStep]) => {
    return $draftFlowCurrentStep ? $draftFlowSelectedData[$draftFlowCurrentStep?.fieldName] : null;
  }
);
export const currentStepHasData = derived(
  [currentStepData, draftFlowCurrentStep],
  ([$currentStepData, $draftFlowCurrentStep]) => {
    if ($draftFlowCurrentStep?.mode === 'single') {
      return $currentStepData !== null;
    } else {
      return ($currentStepData as ActionObj[])?.length > 0;
    }
  }
);

// Derived store: shows steps up to i+1 where i is the last step with selected data
export const draftFlowSteps = derived(
  [completeDraftFlowSteps, draftFlowSelectedData],
  ([$completeDraftFlowSteps, $draftFlowSelectedData]) => {
    if ($completeDraftFlowSteps.length === 0) return [];

    // Find the last step with selected data
    let lastSelectedIndex = -1;
    for (let i = 0; i < $completeDraftFlowSteps.length; i++) {
      const step = $completeDraftFlowSteps[i];
      if ($draftFlowSelectedData[step.fieldName]) {
        lastSelectedIndex = i;
      }
    }

    // Show steps up to and including the next step after the last selected
    const visibleStepsCount = lastSelectedIndex + 2;
    return $completeDraftFlowSteps.slice(0, visibleStepsCount);
  }
);

// Helper to reset all stores
export function resetDraftFlow() {
  selectedActionType.set(null);
  completeDraftFlowSteps.set([]);
  draftFlowSelectedData.set({} as WorkflowSelectedData);
  draftFlowInputContent.set(null);
  draftFlowIsComplete.set(false);
  draftFlowAutoAdvance.set(true);
  draftFlowStartedFromChat.set(false);
}
